/**
 * Normalizes sequence into tree structure with sub-sequences
 * - String items: linear progression to next element
 * - Object with id: branching with onPurchase/onDecline as sub-sequences
 * - Object with upsellPhysical: physical product group (shipping form flow)
 *
 * @param {Array<string|Object>} seq - Sequence array
 * @returns {Array<Object>} Normalized tree structure
 */
function normalizeSequence(seq) {
    if (!seq || !Array.isArray(seq)) {
        return [];
    }
    return seq
        .map((item, index) => {
        // String format (linear): continue to next item in current sequence
        if (typeof item === 'string') {
            // Get the rest of the sequence after this item
            const remainingSeq = seq.slice(index + 1);
            // If there are more items, recursively normalize them as the next sub-sequence
            // Otherwise, null means end of this sequence path
            const nextSequence = remainingSeq.length > 0 ? normalizeSequence(remainingSeq) : null;
            return Object.freeze({
                id: item,
                onPurchase: nextSequence,
                onDecline: nextSequence,
            });
        }
        else if (item && item?.id) {
            const branchItem = item;
            return Object.freeze({
                id: branchItem.id,
                onPurchase: branchItem.onPurchase ? normalizeSequence(branchItem.onPurchase) : null,
                onDecline: branchItem.onDecline ? normalizeSequence(branchItem.onDecline) : null,
            });
        }
        else if (item &&
            item.upsellPhysical &&
            Array.isArray(item.upsellPhysical)) {
            const physicalItem = item;
            const remainingSeq = seq.slice(index + 1);
            const nextSequence = remainingSeq.length > 0 ? normalizeSequence(remainingSeq) : null;
            return Object.freeze({
                id: physicalItem.upsellPhysical[0],
                isPhysical: true,
                physicalList: Object.freeze([...physicalItem.upsellPhysical]),
                onPurchase: nextSequence,
                onDecline: nextSequence,
            });
        }
        return null;
    })
        .filter((node) => node !== null);
}
/**
 *
 * @param {*} id string
 * @param {*} desc string
 * @param {*} seq Array<string|Object> - Can be array of strings (linear) or objects (branching)
 * @returns Upsell
 */
export const createUpsell = ({ id, desc, seq }) => {
    const sequenceTree = normalizeSequence(seq);
    return Object.freeze({
        id,
        description: desc,
        sequence: seq,
        sequenceTree,
    });
};
export const UPSELL_TYPES = Object.freeze({
    fastFoodMeal: 'fastFoodMeal',
    fastFoodMealPilates: 'fastFoodMealPilates',
    sweetTreatsMeal: 'sweetTreatsMeal',
    premiumContent: 'premiumContent',
    alcohol: 'alcohol',
    detox: 'detox',
    '12Month': '12Month',
    '12MonthLifetime': '12MonthLifetime',
    yogaAndHealthyBack: 'yogaAndHealthyBack',
    detoxAndSweetTreats: 'detoxAndSweetTreats',
    chairWorkoutSixPack: 'chairWorkoutSixPack',
    mealPlanSupplementsGuide: 'mealPlanSupplementsGuide',
    coach: 'coach',
    supplements: 'supplements',
    allInOneLifetime: 'allInOneLifetime',
    allInOne1Year: 'allInOne1Year',
});
export const upsellsEnum = Object.freeze({
    yoga: 'yoga',
    healthyBack: 'healthyBack',
    yogaAndHealthyBack: 'yogaAndHealthyBack',
    detox: UPSELL_TYPES.detox,
    sweetTreatsMeal: UPSELL_TYPES.sweetTreatsMeal,
    detoxAndSweetTreats: UPSELL_TYPES.detoxAndSweetTreats,
    chairWorkout: 'chairWorkout',
    sixPack: 'sixPack',
    chairWorkoutSixPack: 'chairWorkoutSixPack',
    mealPlanSupplementsGuide: 'mealPlanSupplementsGuide',
    mealPlan: 'mealPlan',
    supplementsGuide: 'supplementsGuide',
});
